import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Avatar, CircularProgress, Skeleton } from "@mui/material";
import BaseImagesList from "../../../../../components/ui/BaseImagesList";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import BaseTooltip from "../../../../../components/ui/BaseTooltip";
import BaseEditor from "../../../../../components/ui/BaseEditor";
import OpenableLink from "../../../../../components/ui/OpenableLink";
import ChatFileUploadModal from "../ChatFileUploadModal";
import ChatParticipantsModal from "../ChatParticipantsModal";
import "./chatEditor.css";
import { useDispatch } from "react-redux";
import { setWorkOrderChatEditorState } from "../../../../../store/workOrder/workOrderSlice";
import Map from "../../../../../components/Map/Map";
import BaseConfirmModal from "../../../../../components/ui/BaseConfirmModal";
import EmojiPickerLib from "emoji-picker-react";
import { decodeHtmlEntities } from "../../../../../utils/helpers";

const QUICK_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

const BAR_COUNT = 25;

const analyzeWaveform = async (url) => {
  try {
    const res = await fetch(url);
    const buf = await res.arrayBuffer();
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioCtx();
    const decoded = await ctx.decodeAudioData(buf);
    ctx.close();
    const raw = decoded.getChannelData(0);
    const block = Math.floor(raw.length / BAR_COUNT);
    const bars = Array.from({ length: BAR_COUNT }, (_, i) => {
      let sum = 0;
      for (let j = 0; j < block; j++) sum += Math.abs(raw[i * block + j]);
      return sum / block;
    });
    const max = Math.max(...bars, 0.001);
    return bars.map((v) => Math.max(15, Math.round((v / max) * 100)));
  } catch {
    return null;
  }
};

const getSupportedMimeType = () => {
  if (typeof MediaRecorder === "undefined") return "";
  const types = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
  return types.find((t) => MediaRecorder.isTypeSupported(t)) || "";
};

const fmtDuration = (s) => {
  const t = Math.floor(s || 0);
  return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};

const schema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), "u", "b", "strong", "i", "em"],
  attributes: {
    ...(defaultSchema.attributes || {}),
    u: [],
    b: [],
    strong: [],
    i: [],
    em: [],
    a: ["href", "target", "rel"],
  },
};

const BARE_URL = /(https?:\/\/[^\s<]+)/g;
const LINK_AS_TEXT = '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>';
const EXISTING_MARKUP = /(<a\b[^>]*>[\s\S]*?<\/a>|<[^>]+>)/gi;

const makeLinksClickable = (text) => {
  if (!text) return "";
  return text
    .split(EXISTING_MARKUP)
    .map((part, i) => (i % 2 === 1 ? part : part.replace(BARE_URL, LINK_AS_TEXT)))
    .join("");
};

const formattedTime = (createdAt) =>
  createdAt
    ? new Date(createdAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    : "";

// How close to the top of the thread triggers loading the previous page.
const LOAD_OLDER_THRESHOLD_PX = 150;
// Quiet period after a page lands, so one inertial flick loads one page.
const LOAD_OLDER_COOLDOWN_MS = 500;

const groupMessagesByDate = (messages = []) =>
  messages?.reduce((acc, message) => {
    const dateKey = new Date(message.createdAt).toLocaleDateString("en-CA");
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(message);
    return acc;
  }, {});

const formattedDateTime = (createdAt) =>
  createdAt ? `${new Date(createdAt).toLocaleDateString("en-GB")} ${formattedTime(createdAt)}` : "";

// Consecutive messages from one sender collapse into a run: only the first of a
// run carries an avatar and a header line, the rest line up underneath it. The
// name and time are never repeated while the same person keeps talking. Callers
// pass the previous message of the same day, so a new day always starts a run.
// A system line ("X added Y") is not that person talking, so it ends the run:
// otherwise the message after it matched its senderId, counted as a continuation
// and lost its name and avatar.
const startsNewRun = (message, previous) =>
  !previous || previous?.messageType === "system" || message?.senderId !== previous?.senderId;

const getReadableDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "";
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Today";
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString("en-GB");
};

// Today needs no date, yesterday gets the word, anything older carries its date.
const messageTimestamp = (createdAt) => {
  const day = getReadableDate(createdAt);
  if (!day) return "";
  return day === "Today" ? formattedTime(createdAt) : `${day} ${formattedTime(createdAt)}`;
};

const autoLinkText = (text = "") => {
  if (!text) return null;
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[[rehypeRaw], [rehypeSanitize, schema]]}
      components={{
        a: ({ node, ...props }) => (
          <a
            {...props}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 underline break-words"
          />
        ),
        strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
        b: ({ node, ...props }) => <b className="font-semibold" {...props} />,
        em: ({ node, ...props }) => <em className="italic" {...props} />,
        u: ({ node, ...props }) => <u className="underline" {...props} />,
        ol: ({ node, ...props }) => (
          <ol
            className="list-decimal ml-5 text-sm space-y-1"
            style={{ paddingLeft: "1.25rem" }}
            {...props}
          />
        ),
        ul: ({ node, ...props }) => (
          <ul
            className="list-disc ml-5 text-sm space-y-1"
            style={{ paddingLeft: "1.25rem" }}
            {...props}
          />
        ),
        li: ({ node, ...props }) => <li className="break-words" {...props} />,
      }}
    >
      {makeLinksClickable(text)}
    </ReactMarkdown>
  );
};

function LocationLink({ latitude, longitude }) {
  return (
    <div
      onClick={() =>
        window.open(`https://www.google.com/maps?q=${latitude},${longitude}&z=18`, "_blank")
      }
    >
      <div
        style={{
          width: "190px",
          height: "220px",
          borderRadius: "10px",
          overflow: "hidden",
          marginTop: "8px",
        }}
      >
        <Map latitude={latitude} longitude={longitude} />
      </div>
    </div>
  );
}

function ReplyPreview({ replyTo, align = "left" }) {
  if (!replyTo) return null;

  const senderName = `${replyTo.sender?.firstName ?? ""} ${replyTo.sender?.lastName ?? ""}`.trim();
  // Strip the markup, then decode what it was holding. A quote is shown as text,
  // so "&amp;" and the "&nbsp;" that rides along with pasted content would be read
  // out literally. Strip first — decoding first would turn a typed "<b>" into a
  // tag the strip then swallows.
  // Teams shows two lines of the quoted message and ellipses the rest, so keep
  // enough text to fill them without dragging a whole essay into the DOM.
  const preview =
    decodeHtmlEntities(replyTo.message?.replace(/<[^>]+>/g, "") ?? "")
      .trim()
      .slice(0, 240) || "Attachment";

  const handleJump = () => {
    const el = document.getElementById(`message-${replyTo.id}`);
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "center" });

    el.style.background = "#e0e0e5ff";

    setTimeout(() => {
      el.style.background = "";
    }, 1200);
  };

  return (
    <div
      onClick={handleJump}
      className={`cursor-pointer border-l-[3px] border-gray-300 bg-white rounded w-full px-3 py-2 mb-2 max-w-[420px] hover:bg-gray-50 transition ${
        align === "right" ? "ml-auto" : ""
      }`}
    >
      {/* Same quiet meta line as MessageMeta above the bubble. */}
      <p className="truncate mb-0.5">
        <span className="text-[12px] font-light text-gray-600">{senderName}</span>
        <span className="text-[11px] font-light text-gray-600 ml-2">
          {formattedDateTime(replyTo.createdAt)}
        </span>
      </p>
      <p className="text-[13px] font-light text-gray-900 line-clamp-2 break-words whitespace-pre-wrap">
        {preview}
      </p>
    </div>
  );
}
function Reactions({ reactions = [], authUserId, onToggle, align = "left" }) {
  const [showEmoji, setShowEmoji] = useState(false);

  if (!reactions.length) return null;
  const grouped = reactions?.reduce((acc, r) => {
    if (!acc[r.emoji]) acc[r.emoji] = [];
    acc[r.emoji].push(r);
    return acc;
  }, {});

  // leading-none keeps the emoji on the pill's centre line: its glyph carries a
  // tall line box that otherwise pushes it off-centre.
  const pill =
    "inline-flex items-center justify-center gap-0.5 text-xs leading-none px-1.5 py-[4px] rounded-full border transition";

  return (
    <div
      className={`relative z-10 -mt-1 flex flex-wrap items-center gap-1 ${
        align === "right" ? "justify-end mr-3" : "justify-start ml-3"
      }`}
    >
      {Object?.entries(grouped)?.map(([emoji, users]) => {
        const reacted = users.some((u) => u.userId === authUserId);
        const names = users.map((u) => `${u.user?.firstName} ${u.user?.lastName}`).join(", ");
        return (
          <BaseTooltip key={emoji} content={names}>
            <button
              onClick={() => onToggle(emoji)}
              className={`${pill} !bg-white ${
                reacted ? "border-gray-300" : "border-gray-200 hover:!bg-gray-50"
              }`}
            >
              <span className="block leading-none">{emoji}</span>
              {/* A lone reaction needs no "1" next to it. */}
              {users.length > 1 && (
                <span className="block leading-none text-gray-600">{users.length}</span>
              )}
            </button>
          </BaseTooltip>
        );
      })}
      <div className="relative">
        <BaseTooltip content="Add reaction">
          <button
            // The picker closes on document mousedown; without this the close and
            // the toggle cancel out and it can never be dismissed by its own button.
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => setShowEmoji((p) => !p)}
            aria-label="Add reaction"
            className={`${pill} !bg-white border-gray-200 hover:!bg-gray-50 text-gray-500`}
          >
            <i className="fa-light fa-face-smile-plus !text-gray-500" />
          </button>
        </BaseTooltip>
        {showEmoji && (
          <EmojiPicker
            onSelect={(emoji) => {
              onToggle(emoji);
              setShowEmoji(false);
            }}
            onClose={() => setShowEmoji(false)}
          />
        )}
      </div>
    </div>
  );
}

function EmojiPicker({ onSelect, onClose }) {
  const quickBarRef = useRef(null);
  const plusBtnRef = useRef(null);
  const fullPickerRef = useRef(null);
  const [showFullPicker, setShowFullPicker] = useState(false);
  const [pickerPos, setPickerPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handler = (e) => {
      const inQuickBar = quickBarRef.current?.contains(e.target);
      const inFullPicker = fullPickerRef.current?.contains(e.target);
      if (!inQuickBar && !inFullPicker) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const handlePlusClick = () => {
    if (!plusBtnRef.current) return;
    const rect = plusBtnRef.current.getBoundingClientRect();
    const pickerWidth = 310;
    const pickerHeight = 400;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - pickerWidth - 8));
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow >= pickerHeight + 8 ? rect.bottom + 6 : rect.top - pickerHeight - 6;
    setPickerPos({ top, left });
    setShowFullPicker((p) => !p);
  };

  return (
    <>
      <div
        ref={quickBarRef}
        className="absolute z-50 bottom-9 bg-white border border-gray-100 rounded-full shadow-xl px-2 py-1.5 flex items-center gap-0.5"
      >
        {QUICK_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onSelect(emoji);
              onClose();
            }}
            className="w-8 h-8 flex items-center justify-center text-xl rounded-full hover:bg-gray-100 hover:scale-125 transition-all"
          >
            {emoji}
          </button>
        ))}
        <button
          ref={plusBtnRef}
          onClick={handlePlusClick}
          className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 text-base font-bold ml-0.5 transition-colors"
        >
          +
        </button>
      </div>
      {showFullPicker &&
        ReactDOM.createPortal(
          <div
            ref={fullPickerRef}
            style={{ position: "fixed", top: pickerPos.top, left: pickerPos.left, zIndex: 9999 }}
          >
            <EmojiPickerLib
              onEmojiClick={(emojiData) => {
                onSelect(emojiData.emoji);
                onClose();
              }}
              width={310}
              height={400}
              previewConfig={{ showPreview: false }}
              searchPlaceholder="Search emoji…"
              lazyLoadEmojis
            />
          </div>,
          document.body,
        )}
    </>
  );
}

function MessageActions({ isOwn, onReply, onEdit, onDelete, onReact, align = "right" }) {
  const [showEmoji, setShowEmoji] = useState(false);

  return (
    <div
      className={`absolute top-1/2 -translate-y-1/2 ${align === "right" ? "left-0 -translate-x-full pr-2" : "right-0 translate-x-full pl-2"} flex items-center gap-1 transition-opacity ${showEmoji ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
    >
      <div className="relative">
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => setShowEmoji((p) => !p)}
          className="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow hover:bg-gray-50 text-sm"
        >
          😊
        </button>
        {showEmoji && <EmojiPicker onSelect={onReact} onClose={() => setShowEmoji(false)} />}
      </div>
      <button
        onClick={onReply}
        className="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow hover:bg-gray-50 text-xs text-gray-600"
      >
        <i className="fa-solid fa-reply" />
      </button>
      {isOwn && (
        <>
          {onEdit && (
            <button
              onClick={onEdit}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow hover:bg-gray-50 text-xs text-gray-600"
            >
              <i className="fa-light fa-pencil cursor-pointer text-gray-900" />
            </button>
          )}
          <button
            onClick={onDelete}
            className="w-7 h-7 flex items-center justify-center rounded-full bg-white border border-gray-200 shadow hover:bg-red-50 text-xs text-red-400"
          >
            <i className="fa-solid fa-trash" />
          </button>
        </>
      )}
    </div>
  );
}

const cleanEditorHTML = (html = "") => {
  const div = document.createElement("div");
  div.innerHTML = html;

  const isBlankParagraph = (el) =>
    el?.tagName === "P" && !el.textContent.trim() && !el.querySelector("img");

  while (isBlankParagraph(div.firstElementChild)) div.firstElementChild.remove();
  while (isBlankParagraph(div.lastElementChild)) div.lastElementChild.remove();

  return div.innerHTML.trim();
};

const isBlankHTML = (html = "") => {
  if (/<img\b/i.test(html)) return false;
  return decodeHtmlEntities(html.replace(/<[^>]+>/g, "")).replace(/\s/g, "").length === 0;
};

function InlineEdit({ initialValue, allowEmpty = false, onSave, onCancel }) {
  const [value, setValue] = useState(initialValue || "");
  const editorRef = useRef(null);

  useEffect(() => {
    editorRef.current?.focus?.();
  }, []);

  const cleaned = cleanEditorHTML(value);
  const isEmpty = !allowEmpty && isBlankHTML(cleaned);

  return (
    <div className="flex flex-col" style={{ width: "min(90vw, 720px)", maxWidth: "100%" }}>
      {/* The same shell as the composer: toolbar on top, input, then the action
          row inside the border, with the accent line along the bottom. */}
      <div className="chat-inline-edit border border-b-2 border-b-primary rounded-lg overflow-hidden bg-white">
        <BaseEditor
          ref={editorRef}
          wrapperClassName="chat-editor-shell"
          className="w-full text-sm text-light [&_.ql-editor]:text-sm [&_.ql-editor]:font-sans px-4 py-3 resize-none overflow-y-auto focus:outline-none max-h-[200px] border-none"
          value={value}
          onChange={(body) => setValue(body ?? "")}
        />
        <div className="chat-composer-actions bg-white flex items-center justify-end gap-2 px-4 py-2">
          {isEmpty && (
            <span className="text-xs text-gray-400 mr-auto">Empty message can not be saved.</span>
          )}
          <button
            onClick={onCancel}
            className="text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 px-4 py-1.5 rounded-md transition"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(cleaned)}
            disabled={isEmpty}
            className={`text-sm text-white px-5 py-1.5 rounded-md transition ${
              isEmpty ? "bg-primary/40 cursor-not-allowed" : "bg-primary hover:opacity-90"
            }`}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

const SPEEDS = [1, 1.5, 2];

function VoiceMessage({ url }) {
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [waveform, setWaveform] = useState(null);
  const audioRef = useRef(null);
  const waveformRef = useRef(null);
  const isDragging = useRef(false);
  const durationHack = useRef(false);

  useEffect(() => {
    analyzeWaveform(url).then((bars) => {
      if (bars) setWaveform(bars);
    });
  }, [url]);

  useEffect(() => {
    const audio = new Audio(url);
    audio.preload = "auto";
    audioRef.current = audio;

    const onTime = () => setCurrentTime(audio.currentTime);

    const trySetDuration = () => {
      if (isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);
        if (durationHack.current) {
          durationHack.current = false;
          audio.currentTime = 0;
        }
      }
    };

    const onMeta = () => {
      if (audio.duration === Infinity) {
        durationHack.current = true;
        audio.currentTime = 1e100;
      } else {
        trySetDuration();
      }
    };

    const onEnd = () => {
      setPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("durationchange", trySetDuration);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.pause();
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("durationchange", trySetDuration);
      audio.removeEventListener("ended", onEnd);
      audio.src = "";
    };
  }, [url]);

  const seekFromX = (clientX) => {
    if (!waveformRef.current || !audioRef.current || !duration) return;
    const rect = waveformRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const t = ratio * duration;
    audioRef.current.currentTime = t;
    setCurrentTime(t);
  };

  useEffect(() => {
    const up = () => {
      isDragging.current = false;
    };
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.playbackRate = speed;
      audio.play();
    }
    setPlaying((p) => !p);
  };

  const cycleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const progress = duration > 0 ? currentTime / duration : 0;

  return (
    <div className="flex items-center gap-2.5 min-w-[200px] max-w-[260px] py-0.5">
      <button
        onClick={toggle}
        className="w-9 h-9 flex items-center justify-center rounded-full bg-primary text-white flex-shrink-0 shadow"
      >
        <i className={`fa-solid ${playing ? "fa-pause" : "fa-play"} text-xs text-white`} />
      </button>
      <div className="flex-1 flex flex-col gap-1 min-w-0">
        <div
          ref={waveformRef}
          className="relative h-7 flex items-end cursor-pointer select-none"
          style={{ touchAction: "none" }}
          onMouseDown={(e) => {
            isDragging.current = true;
            seekFromX(e.clientX);
          }}
          onMouseMove={(e) => {
            if (isDragging.current) seekFromX(e.clientX);
          }}
          onMouseUp={() => {
            isDragging.current = false;
          }}
          onTouchStart={(e) => {
            isDragging.current = true;
            seekFromX(e.touches[0].clientX);
            e.preventDefault();
          }}
          onTouchMove={(e) => {
            if (isDragging.current) {
              seekFromX(e.touches[0].clientX);
              e.preventDefault();
            }
          }}
          onTouchEnd={() => {
            isDragging.current = false;
          }}
        >
          <div className="absolute inset-0 flex items-end gap-[2px] pointer-events-none">
            {(waveform ?? Array.from({ length: BAR_COUNT }, () => 30)).map((h, i) => (
              <div
                key={i}
                className={`flex-1 rounded-full ${(i + 1) / BAR_COUNT <= progress ? "bg-primary" : "bg-gray-300"}`}
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-gray-400 tabular-nums">
            {fmtDuration(playing ? currentTime : duration)}
          </span>
          <button
            onClick={cycleSpeed}
            className="text-[10px] font-semibold text-primary bg-blue-50 px-1.5 py-0.5 rounded hover:bg-blue-100 transition"
          >
            {speed}x
          </button>
        </div>
      </div>
    </div>
  );
}

function VoiceRecorder({ onSend, onCancel }) {
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const animFrameRef = useRef(null);
  const barsRef = useRef([]);
  const durationRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;

        // Live waveform via Web Audio API
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const audioCtx = new AudioCtx();
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 64; // 32 frequency bins
        audioCtx.createMediaStreamSource(stream).connect(analyser);
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        const barCount = BAR_COUNT;
        const step = Math.max(1, Math.floor(dataArray.length / barCount));

        const draw = () => {
          analyser.getByteFrequencyData(dataArray);
          for (let i = 0; i < barCount; i++) {
            const el = barsRef.current[i];
            if (el) {
              const val = dataArray[Math.min(i * step, dataArray.length - 1)] || 0;
              el.style.height = `${Math.max(15, (val / 255) * 100)}%`;
            }
          }
          animFrameRef.current = requestAnimationFrame(draw);
        };
        draw();

        // Recording
        const mimeType = getSupportedMimeType();
        const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
        mediaRecorderRef.current = mr;
        chunksRef.current = [];
        mr.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };
        mr.start(100);

        timerRef.current = setInterval(() => {
          durationRef.current += 1;
          setDuration((d) => d + 1);
        }, 1000);
      } catch {
        onCancel();
      }
    };
    start();
    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
      cancelAnimationFrame(animFrameRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const stop = (send) => {
    clearInterval(timerRef.current);
    cancelAnimationFrame(animFrameRef.current);
    const mr = mediaRecorderRef.current;
    if (!mr || mr.state === "inactive") {
      if (!send) onCancel();
      return;
    }
    mr.onstop = () => {
      if (send) {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        onSend(blob, durationRef.current);
      } else {
        onCancel();
      }
    };
    mr.stop();
    streamRef.current?.getTracks().forEach((t) => t.stop());
  };

  return (
    <div className="mb-4 mx-3 lg:mx-10 border rounded-lg bg-white overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3">
        <button onClick={() => stop(false)} className="text-gray-400 hover:text-red-400 transition">
          <i className="fa-solid fa-xmark text-xl" />
        </button>
        <div className="flex-1 flex items-center gap-2 bg-blue-50 rounded-full px-3 py-1.5">
          <span className="w-2 h-2 rounded-full bg-primary animate-pulse flex-shrink-0" />
          <span className="text-sm text-primary font-mono font-medium tabular-nums w-11">
            {fmtDuration(duration)}
          </span>
          {/* Live animated bars — heights updated directly via refs, no React re-render */}
          <div className="flex-1 flex items-end gap-[2px] h-8">
            {Array.from({ length: BAR_COUNT }, (_, i) => (
              <div
                key={i}
                ref={(el) => {
                  barsRef.current[i] = el;
                }}
                className="flex-1 bg-primary rounded-full"
                style={{ height: "15%" }}
              />
            ))}
          </div>
        </div>
        <button
          onClick={() => stop(true)}
          className="bg-primary rounded-full w-9 h-9 flex items-center justify-center text-white shadow hover:opacity-90 transition flex-shrink-0"
        >
          <i className="fa-solid fa-paper-plane text-sm text-white" />
        </button>
      </div>
    </div>
  );
}

// The read-receipt tick sits after an own message's bubble, so the bubble stops
// short of the row's right edge. The meta line above it is inset by the same
// amount to finish level with the bubble rather than with the row.
// fa-check-double is 1.25em wide; at text-xs (12px) that is 15px, plus ml-2 (8px).
const READ_TICK_GUTTER = 23;

function MessageMeta({ name, profileLink, createdAt, isEdited, align = "left", inset = 52 }) {
  // The line Teams puts above a message: who, when, and whether it was edited.
  // The name matches the time beside it — one quiet meta line, as in Teams.
  const nameClass = "text-[12px] font-light text-gray-600";
  return (
    <div
      className={`flex items-baseline gap-2 mb-1 ${align === "right" ? "justify-end" : ""}`}
      style={align === "right" ? { paddingRight: inset } : { paddingLeft: inset }}
    >
      {!name ? null : profileLink ? (
        <OpenableLink
          as="span"
          to={profileLink}
          className={`${nameClass} cursor-pointer hover:text-primary hover:underline`}
        >
          {name}
        </OpenableLink>
      ) : (
        <span className={nameClass}>{name}</span>
      )}
      <span className="text-[11px] font-light text-gray-600">{messageTimestamp(createdAt)}</span>
      {isEdited && <span className="text-[11px] font-light text-gray-400">Edited</span>}
    </div>
  );
}

// Holds a grouped message in line with the text above it: the run's header line
// already says who sent it and when.
function Gutter() {
  return <div className="w-10 shrink-0" aria-hidden="true" />;
}

function LeftBubble({
  message,
  messageType,
  files = [],
  createdAt,
  sender,
  replyTo,
  reactions,
  isEdited,
  isDeleted,
  authUserId,
  onReply,
  onReact,
  profileLink,
  isRunStart = true,
}) {
  const showFiles = !isDeleted && files?.length > 0 && messageType !== "voice";
  const showBody = isDeleted || !!message || messageType === "voice";
  if (!showFiles && !showBody) return null;

  return (
    <div className={`group/row w-full max-w-[900px] ${isRunStart ? "mt-5" : "mt-0.5"}`}>
      {isRunStart && (
        <MessageMeta
          name={`${sender?.firstName ?? ""} ${sender?.lastName ?? ""}`.trim()}
          profileLink={profileLink}
          createdAt={createdAt}
          isEdited={!isDeleted && isEdited}
        />
      )}
      <div className="flex gap-3">
        {isRunStart ? (
          profileLink ? (
            <OpenableLink as="div" to={profileLink} className="cursor-pointer shrink-0">
              <Avatar src={sender?.profilePicture} />
            </OpenableLink>
          ) : (
            <Avatar src={sender?.profilePicture} className="shrink-0" />
          )
        ) : (
          <Gutter />
        )}
        <div className="min-w-0 flex-1">
          <div className="relative group bg-[#F5F5F5] rounded-lg px-4 py-3 w-fit max-w-full flex flex-col">
            {!isDeleted && (
              <MessageActions isOwn={false} onReply={onReply} onReact={onReact} align="left" />
            )}
            {replyTo && <ReplyPreview replyTo={replyTo} align="left" />}
            {showFiles && (
              <ul className="flex flex-wrap gap-2 max-w-full">
                <BaseImagesList files={files} />
              </ul>
            )}
            {isDeleted ? (
              <p className="text-xs italic text-gray-400">This message was deleted</p>
            ) : messageType === "voice" ? (
              <VoiceMessage url={files?.[0]?.url} />
            ) : (
              message && (
                <div className="mr-4 text-[13px] font-light text-gray-900 [&_p]:text-[13px] [&_p]:font-light [&_p]:text-gray-900 break-all whitespace-pre-wrap">
                  {messageType === "location"
                    ? (() => {
                        const [lat, lng] = message.split(",").map(Number);
                        return isNaN(lat) ? (
                          <p>Invalid location</p>
                        ) : (
                          <LocationLink latitude={lat} longitude={lng} />
                        );
                      })()
                    : autoLinkText(message)}
                </div>
              )
            )}
            {/* The run's first message carries "Edited" in its header instead. */}
            {!isRunStart && !isDeleted && isEdited && (
              <span className="text-[10px] text-gray-400 italic mt-1">edited</span>
            )}
          </div>
          <Reactions reactions={reactions} authUserId={authUserId} onToggle={onReact} />
        </div>
      </div>
    </div>
  );
}

function RightBubble({
  message,
  messageType,
  files,
  createdAt,
  status,
  replyTo,
  reactions,
  isEdited,
  isDeleted,
  authUserId,
  onReply,
  onReact,
  onEdit,
  onDelete,
  profileLinkFor,
  isRunStart = true,
}) {
  const [editing, setEditing] = useState(false);

  // One bubble per message — see LeftBubble.
  const showFiles = !isDeleted && files?.length > 0 && messageType !== "voice";
  const showBody = isDeleted || !!message || messageType === "voice";
  if (!showFiles && !showBody) return null;

  // A thread carries one status row per recipient. The tick turns primary as
  // soon as ANY of them has read it, matching the names the tooltip lists.
  const isReadByAnyone = status?.some((s) => s?.isRead) ?? false;

  return (
    <div className={`group/row w-full max-w-[900px] ml-auto ${isRunStart ? "mt-5" : "mt-0.5"}`}>
      {/* Your own messages need no name or picture to say who sent them. */}
      {isRunStart && (
        <MessageMeta
          createdAt={createdAt}
          isEdited={!isDeleted && isEdited}
          align="right"
          inset={READ_TICK_GUTTER}
        />
      )}
      <div className="flex gap-3 justify-end">
        <div className="min-w-0 flex-1 flex flex-col items-end">
          <div className="flex items-start justify-end w-full">
            <div className="relative group bg-[#EAF0FF] rounded-lg px-4 py-3 w-fit max-w-full">
              {!isDeleted && (
                <MessageActions
                  isOwn={true}
                  onReply={onReply}
                  onReact={onReact}
                  onEdit={messageType !== "voice" ? () => setEditing(true) : undefined}
                  onDelete={onDelete}
                  align="right"
                />
              )}
              {replyTo && <ReplyPreview replyTo={replyTo} align="right" />}
              {showFiles && (
                <ul className="flex flex-wrap gap-2 max-w-full">
                  <BaseImagesList files={files} />
                </ul>
              )}
              {isDeleted ? (
                <p className="text-xs italic text-gray-400">This message was deleted</p>
              ) : messageType === "voice" ? (
                <VoiceMessage url={files?.[0]?.url} />
              ) : editing ? (
                <InlineEdit
                  initialValue={message}
                  allowEmpty={showFiles}
                  onSave={(val) => {
                    onEdit(val);
                    setEditing(false);
                  }}
                  onCancel={() => setEditing(false)}
                />
              ) : (
                message && (
                  <div className="text-[13px] font-light text-gray-900 [&_p]:text-[13px] [&_p]:font-light [&_p]:text-gray-900 break-all whitespace-pre-wrap max-w-full">
                    {messageType === "location"
                      ? (() => {
                          const [lat, lng] = message.split(",").map(Number);
                          return isNaN(lat) ? (
                            <p>Invalid location</p>
                          ) : (
                            <LocationLink latitude={lat} longitude={lng} />
                          );
                        })()
                      : autoLinkText(message)}
                  </div>
                )
              )}
              {!editing && !isRunStart && !isDeleted && isEdited && (
                <span className="block text-right text-[10px] text-gray-400 italic mt-1">
                  edited
                </span>
              )}
            </div>
            <BaseTooltip
              placement="left"
              interactive={true}
              content={
                <div>
                  <p className="text-white">Seen by:</p>
                  {status?.map((s) => {
                    if (!s?.isRead) return null;
                    // The tooltip is interactive, so these names can be reached and
                    // opened like any other participant in the thread.
                    const link = profileLinkFor?.({ ...s?.user, id: s?.userId });
                    const name = `${s?.user?.firstName} ${s?.user?.lastName}`;
                    return link ? (
                      <OpenableLink
                        as="p"
                        to={link}
                        key={s?.userId}
                        className="text-white cursor-pointer hover:underline"
                      >
                        {name}
                      </OpenableLink>
                    ) : (
                      <p className="text-white" key={s?.userId}>
                        {name}
                      </p>
                    );
                  })}
                </div>
              }
            >
              <i
                className={`fa-solid fa-check-double text-xs cursor-pointer ml-2 mt-3 ${
                  isReadByAnyone ? "text-primary" : "text-gray-400"
                }`}
              />
            </BaseTooltip>
          </div>
          {/* Same gutter as the meta line: keep the pills level with the bubble,
              not with the tick that now follows it. */}
          <div className="w-full" style={{ paddingRight: READ_TICK_GUTTER }}>
            <Reactions
              reactions={reactions}
              authUserId={authUserId}
              onToggle={onReact}
              align="right"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const Chat = ({
  sendMessage,
  handleMessage,
  handleFileChange,
  handleRemoveFile,
  messages,
  message,
  lastUnreadMessageId,
  loadOlderMessages,
  hasMoreMessages = false,
  isFetchingOlderMessages = false,
  selectedFiles,
  setSelectedFiles,
  handleDone,
  authUser,
  selectedUser,
  setSelectedUser,
  company,
  isCompanyOwner = false,
  isLoading = true,
  isUploadMessageFilesMutationApiLoading,
  setShowEditor,
  showEditor,
  workOrderId,
  replyTo,
  setReplyTo,
  onEditMessage,
  onDeleteMessage,
  onToggleReaction,
  onSendVoice,
  isDisabled = false,
  disabledMessage,
  isWorkOrderChat = false,
  freelanceCompanyName = null,
  canManageParticipants = false,
  participantCustomerId = null,
  participantIds = [],
  participants = [],
  onParticipantsChanged,
  conversationId,
  freelanceCompanyLogo = null,
}) => {
  const dispatch = useDispatch();
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatRef = useRef(null);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [isParticipantsModalOpen, setIsParticipantsModalOpen] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const prevMessageRef = useRef(message);
  const [hasSentMessage, setHasSentMessage] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const hasScrolledToUnreadRef = useRef(false);
  const prevLastMessageIdRef = useRef(null);
  const unreadDividerIdRef = useRef(null);
  const profileLinkFor = (user) =>
    user?.id && user.id !== authUser?.id ? `/user/details/profile/${user.id}` : null;

  // The work order chat is a group thread about the job — there is no single
  // counterpart to open a profile for.
  const counterpartProfileLink = isWorkOrderChat ? null : profileLinkFor(selectedUser);

  // A tech working through a freelance company is shown as that company.
  const counterpartName =
    freelanceCompanyName ?? `${selectedUser?.firstName ?? ""} ${selectedUser?.lastName ?? ""}`;
  // The picture must come from whatever the name came from: a thread titled with
  // a company carries that company's logo, never the individual's photo.
  // freelanceCompanyLogo is the company's own logo (the row it comes from is the
  // row the name comes from), so there is nothing to fall back to: a personal
  // photo under a company name is the mismatch this avoids.
  const counterpartAvatar = freelanceCompanyName
    ? freelanceCompanyLogo
    : selectedUser?.picture?.url || selectedUser?.profilePicture;
  // Remount the avatar when the thread or its image changes. Reconciling keeps the
  // same <img> node and only swaps its src, and a browser goes on painting the
  // image it has already decoded until the new one arrives — so switching threads
  // showed the previous person's photo under the new name for a beat. A fresh node
  // starts empty and falls back to the initials until the real image loads.
  const counterpartAvatarKey = `${selectedUser?.id ?? "none"}:${counterpartAvatar ?? "none"}`;
  const editorRef = useRef(null);
  const pendingScrollAnchorRef = useRef(null);
  const loadingOlderRef = useRef(false);
  const loadOlderCooldownUntilRef = useRef(0);
  useEffect(() => {
    if (replyTo) {
      setTimeout(() => {
        editorRef.current?.focus?.();
      }, 0);
    }
  }, [replyTo]);
  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;

    const handleScroll = () => {
      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;

      setShowScrollBottom(!isNearBottom);
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, []);

  // Pull exactly one older page. The ref lock is set synchronously because several
  // gesture events can fire before React re-renders with the fetching flag on.
  const requestOlderMessages = useCallback(() => {
    const el = chatRef.current;
    if (!el || loadingOlderRef.current || !hasMoreMessages || !loadOlderMessages) return;
    // Momentum/inertial scrolling keeps firing events after a page lands; without a
    // cooldown one flick would walk back through several pages.
    if (Date.now() < loadOlderCooldownUntilRef.current) return;

    loadingOlderRef.current = true;
    // Distance from the bottom — the layout effect below re-derives scrollTop from it
    // once the older messages are prepended, so the view stays on the same message.
    pendingScrollAnchorRef.current = el.scrollHeight - el.scrollTop;
    Promise.resolve(loadOlderMessages()).finally(() => {
      loadingOlderRef.current = false;
      loadOlderCooldownUntilRef.current = Date.now() + LOAD_OLDER_COOLDOWN_MS;
    });
  }, [hasMoreMessages, loadOlderMessages]);

  // Paginate on the user's own scroll gesture rather than on the `scroll` event.
  // Restoring the anchor below assigns scrollTop, which itself emits `scroll`; keying
  // off that would fire another load, and another, until the room was drained.
  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;

    const onWheel = (e) => {
      if (e.deltaY < 0 && el.scrollTop < LOAD_OLDER_THRESHOLD_PX) requestOlderMessages();
    };
    const onTouchMove = () => {
      if (el.scrollTop < LOAD_OLDER_THRESHOLD_PX) requestOlderMessages();
    };

    el.addEventListener("wheel", onWheel, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchmove", onTouchMove);
    };
  }, [requestOlderMessages]);

  // Keep the viewport anchored to the same message after a page is prepended,
  // instead of letting the browser jump.
  useLayoutEffect(() => {
    const el = chatRef.current;
    const anchor = pendingScrollAnchorRef.current;
    if (!el || anchor == null || isFetchingOlderMessages) return;

    pendingScrollAnchorRef.current = null;
    el.scrollTop = el.scrollHeight - anchor;
  }, [messages, isFetchingOlderMessages]);
  useEffect(() => {
    if (lastUnreadMessageId && !unreadDividerIdRef.current) {
      unreadDividerIdRef.current = lastUnreadMessageId;
    }
  }, [lastUnreadMessageId]);

  useEffect(() => {
    hasScrolledToUnreadRef.current = false;
    prevLastMessageIdRef.current = null;
    unreadDividerIdRef.current = null;
    pendingScrollAnchorRef.current = null;
    loadingOlderRef.current = false;
    loadOlderCooldownUntilRef.current = 0;
  }, [selectedUser?.id]);

  useEffect(() => {
    if (!chatRef.current || !messages.length) return;

    const el = chatRef.current;
    const lastMessageId = messages[messages.length - 1]?.id;

    if (lastUnreadMessageId && !hasScrolledToUnreadRef.current) {
      const node = document.getElementById(`message-${lastUnreadMessageId}`);
      if (node) {
        hasScrolledToUnreadRef.current = true;
        prevLastMessageIdRef.current = lastMessageId;
        const prev = node.previousElementSibling;
        (prev || node).scrollIntoView({ block: "start", behavior: "smooth" });
        return;
      }
      return;
    }

    const prevId = prevLastMessageIdRef.current;
    const isFirstLoad = !prevId;
    const isNewMessage = !!prevId && prevId !== lastMessageId;

    prevLastMessageIdRef.current = lastMessageId;

    if (isFirstLoad || isNewMessage) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, lastUnreadMessageId]);

  const handleKeyDown = (e) => {
    if (!e) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      // Attachments alone are a valid message — an image pasted with no caption used
      // to leave Enter doing nothing, the same gap that hid the Send button.
      if (message.trim() || selectedFiles?.length) {
        const cleaned = cleanEditorHTML(message);
        sendMessage(cleaned);
        setHasSentMessage(true);
        prevMessageRef.current = message;
      }
    }
  };
  const scrollToBottom = () => {
    const el = chatRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };
  useEffect(() => {
    if (message === "" && textareaRef.current) textareaRef.current.style.height = "auto";
  }, [message]);
  // A pasted screenshot arrives as a clipboard file, which the editor either drops
  // or inlines as a full-size base64 <img>. Either way `selectedFiles` never changes
  // and, with no text typed, the composer keeps showing the microphone instead of
  // Send. Divert image pastes to the same handler the attach button uses so they
  // land in the preview strip and upload as real attachments.
  const handleEditorPaste = (event) => {
    const clipboard = event.clipboardData;
    if (!clipboard) return;

    const files = Array.from(clipboard.files ?? []).filter((file) =>
      file?.type?.startsWith("image/"),
    );
    // Some browsers expose a pasted screenshot only through `items`.
    const fromItems = files.length
      ? []
      : Array.from(clipboard.items ?? [])
          .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
          .map((item) => item.getAsFile())
          .filter(Boolean);

    const images = files.length ? files : fromItems;
    if (!images.length) return;

    event.preventDefault();
    handleFileChange(images);
  };

  return (
    <div className="bg-[#FDFDFF] flex flex-col h-screen lg:h-[70vh] overflow-x-hidden">
      <div
        className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden relative w-full"
        ref={chatRef}
      >
        <div className="flex items-center justify-between bg-white border-b-[1px] h-[70px] sticky top-0 z-20 w-full px-4">
          <div className="flex items-center">
            {isWorkOrderChat ? (
              <>
                <Avatar>
                  <i className="fa-light fa-clipboard-list text-base !text-white" />
                </Avatar>
                {/* Named after the job only. The parties are deliberately not
                    spelled out: the tech must not learn who the customer is. */}
                <h5 className="font-semibold text-gray-900 ml-3 text-base">
                  {selectedUser?.firstName || "Job Discussion"}
                </h5>
              </>
            ) : authUser?.role?.role === "CLIENT" ||
              (isCompanyOwner && selectedUser?.id !== authUser?.id) ? (
              counterpartProfileLink ? (
                <OpenableLink
                  as="div"
                  to={counterpartProfileLink}
                  className="flex items-center cursor-pointer group"
                >
                  <Avatar key={counterpartAvatarKey} src={counterpartAvatar} />
                  <h5 className="font-semibold text-gray-900 ml-3 capitalize text-base group-hover:text-primary group-hover:underline">{counterpartName}</h5>
                </OpenableLink>
              ) : (
                <>
                  <Avatar key={counterpartAvatarKey} src={counterpartAvatar} />
                  <h5 className="font-semibold text-gray-900 ml-3 capitalize text-base">{counterpartName}</h5>
                </>
              )
            ) : freelanceCompanyName ? (
              // A vendor's tech: the thread is with their vendor, not TechDomain.
              <>
                <Avatar key={counterpartAvatarKey} src={counterpartAvatar} />
                <h5 className="font-semibold text-gray-900 ml-3 capitalize text-base">
                  {freelanceCompanyName}
                </h5>
              </>
            ) : counterpartProfileLink ? (
              // Engineers see the client's company name rather than the person, but
              // the thread is still with selectedUser - so that is where it opens.
              <OpenableLink
                as="div"
                to={counterpartProfileLink}
                className="flex items-center cursor-pointer group"
              >
                <Avatar src={company?.logo} />
                <h5 className="font-semibold text-gray-900 ml-3 capitalize text-base group-hover:text-primary group-hover:underline">
                  {company?.name}
                </h5>
              </OpenableLink>
            ) : (
              <>
                <Avatar src={company?.logo} />
                <h5 className="font-semibold text-gray-900 ml-3 capitalize text-base">{company?.name}</h5>
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            {canManageParticipants && (
              <BaseTooltip content="People in this chat">
                <i
                  className="fa-light fa-user-plus text-lg cursor-pointer text-gray-500 hover:text-primary transition"
                  onClick={() => setIsParticipantsModalOpen(true)}
                />
              </BaseTooltip>
            )}
            {selectedUser && (
              <i
                className="fa-solid fa-arrow-left-long lg:hidden cursor-pointer"
                onClick={() => setSelectedUser(null)}
              />
            )}
          </div>
        </div>

        <div className="grid justify-items-start py-4 lg:px-10 px-4 w-full min-w-0">
          {!isLoading && (isFetchingOlderMessages || hasMoreMessages) && (
            <div className="flex justify-center w-full py-2">
              {isFetchingOlderMessages ? (
                <i className="fa-solid fa-circle-notch fa-spin text-gray-400" />
              ) : (
                // Also the way out when the thread is too short to scroll at all.
                <button
                  type="button"
                  onClick={requestOlderMessages}
                  className="text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-full px-3 py-1 bg-white"
                >
                  Load older messages
                </button>
              )}
            </div>
          )}
          {isLoading
            ? Array(5)
                .fill()
                .map((_, i) =>
                  i % 2 === 0 ? (
                    <LeftBubbleSkeleton key={i} />
                  ) : (
                    <div key={i} className="flex justify-end w-full">
                      <RightBubbleSkeleton />
                    </div>
                  ),
                )
            : Object.entries(groupMessagesByDate(messages)).map(([date, msgs]) => (
                <div key={date} className="mb-6 w-full">
                  <div
                    className="sticky top-[80px] z-10 flex justify-center my-4 w-full"
                    style={{ pointerEvents: "none" }}
                  >
                    <span className="bg-gray-300 text-gray-700 px-3 py-1 rounded-full text-xs">
                      {getReadableDate(date)}
                    </span>
                  </div>
                  {Array.isArray(msgs) &&
                    msgs.map((msg, index) => {
                      const isUnreadDivider =
                        unreadDividerIdRef.current && msg.id === unreadDividerIdRef.current;
                      const isOwn = authUser?.id === msg?.senderId;
                      // The "Last Read" line splits the thread, so the message under
                      // it opens a run of its own.
                      const isRunStart =
                        (!hasSentMessage && isUnreadDivider) || startsNewRun(msg, msgs[index - 1]);
                      return (
                        <div key={msg.id} id={`message-${msg.id}`} className="w-full">
                          {!hasSentMessage && isUnreadDivider && (
                            <div className="flex items-center my-4 w-full">
                              <div className="flex-grow border-t border-gray-300" />
                              <span className="mx-3 text-gray-500 text-xs">Last Read</span>
                              <div className="flex-grow border-t border-gray-300" />
                            </div>
                          )}
                          {msg?.messageType === "system" ? (
                            // Membership changes are a line in the thread, not a
                            // message from anyone — centred, no bubble, no avatar.
                            <div className="flex justify-center w-full my-3">
                              <span className="rounded-full bg-gray-100 px-3 py-1 text-[11px] font-light text-gray-600 text-center">
                                {msg.message}
                              </span>
                            </div>
                          ) : isOwn ? (
                            <RightBubble
                              key={msg.id}
                              status={msg.chatMessageReadStatus}
                              message={msg.message}
                              messageType={msg?.messageType}
                              files={msg.files}
                              createdAt={msg.createdAt}
                              replyTo={msg.replyTo}
                              reactions={msg.reactions ?? []}
                              isEdited={msg.isEdited}
                              isDeleted={msg.isDeleted}
                              authUserId={authUser?.id}
                              isRunStart={isRunStart}
                              profileLinkFor={profileLinkFor}
                              onReply={() => {
                                setReplyTo(msg);
                              }}
                              onReact={(emoji) => {
                                onToggleReaction(msg.id, emoji, msg.conversationId);
                              }}
                              onEdit={(newContent) => {
                                onEditMessage(msg.id, newContent, msg.conversationId);
                              }}
                              onDelete={() => {
                                setConfirmDelete({
                                  id: msg.id,
                                  conversationId: msg.conversationId,
                                });
                              }}
                            />
                          ) : (
                            <LeftBubble
                              key={msg.id}
                              message={msg.message}
                              messageType={msg?.messageType}
                              files={msg.files}
                              sender={msg.sender}
                              profileLink={profileLinkFor(msg.sender)}
                              isRunStart={isRunStart}
                              createdAt={msg.createdAt}
                              replyTo={msg.replyTo}
                              reactions={msg.reactions ?? []}
                              isEdited={msg.isEdited}
                              isDeleted={msg.isDeleted}
                              authUserId={authUser?.id}
                              onReply={() => {
                                setReplyTo(msg);
                              }}
                              onReact={(emoji) => {
                                onToggleReaction(msg.id, emoji, msg.conversationId);
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                </div>
              ))}
        </div>
      </div>
      <div className="flex flex-col relative">
        {" "}
        {showScrollBottom && (
          <button
            onClick={scrollToBottom}
            className="absolute -top-12 right-6 bg-white shadow-lg border rounded-full w-10 h-10 flex items-center justify-center z-50 hover:bg-gray-50 transition"
          >
            ↓
          </button>
        )}
        {isDisabled ? (
          // The thread stays fully readable; only sending is gone — the way a
          // WhatsApp group you are no longer part of keeps its history.
          <div className="mb-4 mx-3 lg:mx-10 flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-[#F5F5F5] px-4 py-4 text-center">
            <i className="fa-light fa-lock text-sm !text-gray-500" />
            <p className="text-[13px] font-light text-gray-600">
              {disabledMessage ||
                "This chat has been closed. You can still read the conversation."}
            </p>
          </div>
        ) : isRecording ? (
          <VoiceRecorder
            onSend={(blob, dur) => {
              onSendVoice(blob, dur);
              setIsRecording(false);
            }}
            onCancel={() => setIsRecording(false)}
          />
        ) : (
          <>
            <div
              className="chat-message-editor mb-4 mx-3 lg:mx-10 border border-b-2 border-b-primary rounded-lg overflow-hidden bg-white max-w-full"
              onPaste={handleEditorPaste}
            >
              {replyTo && (
                <div className="chat-reply-card mx-4 mt-3 mb-0 w-fit max-w-[calc(100%-2rem)] rounded-md border border-gray-200 border-l-4 border-l-gray-300 bg-[#FAFAFA] px-3 py-2">
                  {/* Name, time, then the way out — the order Teams uses. */}
                  <div className="flex items-center gap-2 mb-0.5 min-w-0">
                    <span className="text-[12px] font-light text-gray-600 truncate">{`${replyTo.sender?.firstName} ${replyTo.sender?.lastName}`}</span>
                    <span className="mr-auto text-[11px] font-light text-gray-600 whitespace-nowrap">
                      {formattedDateTime(replyTo.createdAt)}
                    </span>
                    <button
                      onClick={() => setReplyTo(null)}
                      aria-label="Cancel reply"
                      className="group/close shrink-0"
                    >
                      {/* Colour sits on the icon: a global `i { color }` rule in
                          index.css outranks anything inherited from the button. */}
                      <i className="fa-light fa-xmark text-base !text-gray-500 transition-colors group-hover/close:!text-gray-900" />
                    </button>
                  </div>
                  <p className="text-[13px] font-light text-gray-900 line-clamp-2 break-words whitespace-pre-wrap">
                    {decodeHtmlEntities(replyTo.message?.replace(/<[^>]+>/g, "") ?? "")
                      .trim()
                      .slice(0, 240) || "Attachment"}
                  </p>
                </div>
              )}
              <FilePreviewList files={selectedFiles} onRemove={handleRemoveFile} />
              <BaseEditor
                ref={editorRef}
                wrapperClassName="chat-editor-shell"
                className="w-full text-sm text-light [&_.ql-editor]:text-sm [&_.ql-editor]:font-sans px-4 py-3 resize-none overflow-y-auto focus:outline-none max-h-[200px] border-none"
                value={message}
                onKeyDown={handleKeyDown}
                onChange={(body) => {
                  if (!body) {
                    handleMessage({ target: { value: "" } });
                    return;
                  }
                  let cleanHTML = cleanEditorHTML(body);
                  if (cleanHTML !== message) {
                    handleMessage({ target: { value: cleanHTML } });
                    prevMessageRef.current = cleanHTML;
                  }
                }}
                showToolbar={showEditor}
                sendMessage={sendMessage}
              />
              <div className="chat-composer-actions bg-white flex items-center justify-end px-4 py-2 space-x-5">
                <BaseTooltip content="Show Formatting Options">
                  <i
                    className={`fa-light fa-pencil text-lg cursor-pointer hover:text-primary transition ${showEditor ? "text-primary" : "text-gray-500"}`}
                    onClick={() => {
                      setShowEditor((prev) => !prev);
                      dispatch(
                        setWorkOrderChatEditorState({ workOrderId, showEditor: !showEditor }),
                      );
                    }}
                  />
                </BaseTooltip>
                {!isUploadMessageFilesMutationApiLoading && (
                  <BaseTooltip content="Add files">
                    <i
                      className="fa-solid fa-plus text-lg cursor-pointer hover:text-primary text-gray-500 transition"
                      onClick={() => setIsFileModalOpen(true)}
                    />
                  </BaseTooltip>
                )}
                {isUploadMessageFilesMutationApiLoading ? (
                  <div className="bg-primary text-white px-3 py-2 rounded-xl flex items-center">
                    <CircularProgress className="mt-1" sx={{ color: "white" }} size="18px" />
                  </div>
                ) : !message && !selectedFiles?.length ? (
                  <BaseTooltip content="Voice message">
                    <button
                      onClick={() => setIsRecording(true)}
                      className="cursor-pointer bg-primary rounded-xl px-4 py-2 inline-flex items-center justify-center text-white hover:opacity-90 transition"
                    >
                      <i className="fa-solid fa-microphone text-lg text-white" />
                    </button>
                  </BaseTooltip>
                ) : (
                  <BaseTooltip content="Send">
                    <i
                      onClick={sendMessage}
                      className="cursor-pointer bg-primary rounded-xl px-[27px] py-2 inline-flex items-center justify-center fa-solid fa-paper-plane text-lg text-white"
                    />
                  </BaseTooltip>
                )}
              </div>
              {isFileModalOpen && (
                <ChatFileUploadModal
                  open={isFileModalOpen}
                  close={() => setIsFileModalOpen(false)}
                  handleFileChange={handleFileChange}
                  setSelectedFiles={setSelectedFiles}
                  fileInputRef={fileInputRef}
                  triggerFileSelect={() => fileInputRef.current.click()}
                  handleDone={(tempFiles) => {
                    handleDone(tempFiles);
                    editorRef.current?.focus?.();
                  }}
                />
              )}
            </div>
          </>
        )}
      </div>
      {canManageParticipants && (
        <ChatParticipantsModal
          open={isParticipantsModalOpen}
          close={() => setIsParticipantsModalOpen(false)}
          chatRoomId={conversationId}
          existingUserIds={participantIds}
          customerId={participantCustomerId}
          currentUserId={authUser?.id}
          participants={participants}
          viewerRole={authUser?.role?.role ?? null}
          onChanged={onParticipantsChanged}
        />
      )}
      <BaseConfirmModal
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => {
          onDeleteMessage(confirmDelete.id, confirmDelete.conversationId);
          setConfirmDelete(null);
        }}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonColor="error"
        type="warning"
        title="Delete Message"
        description="Are you sure you want to delete this message? This cannot be undone."
      />
    </div>
  );
};

export default Chat;

const FilePreviewList = ({ files = [], onRemove }) => {
  useEffect(() => {
    return () => {
      files.forEach((f) => f.previewURL && URL.revokeObjectURL(f.previewURL));
    };
  }, [files]);

  return (
    <ul className="chat-attachments flex flex-wrap gap-2 mx-4 mt-3 mb-1">
      {files?.map((fileObj, index) => {
        const file = fileObj.file || fileObj;
        const previewURL = fileObj.previewURL;
        const isImage = file.type?.startsWith("image/");
        const name = file.name?.toLowerCase() ?? "";
        const isPDF = file.type === "application/pdf" || name.endsWith(".pdf");
        const isDoc = /\.docx?$/.test(name);
        const isSheet = /\.(csv|xlsx?|xlsm)$/.test(name);
        return (
          <li
            key={fileObj.previewURL || file.name + file.size}
            className="relative w-20 h-20 rounded overflow-hidden group border bg-gray-100"
          >
            <div className="w-full h-full relative">
              {isImage && previewURL ? (
                <img src={previewURL} alt={file.name} className="object-cover w-full h-full" />
              ) : (
                <div className="flex items-center justify-center w-full h-full text-gray-600 text-2xl">
                  {isPDF ? (
                    <i className="fa-solid fa-file-pdf text-red-500" />
                  ) : isDoc ? (
                    <i className="fa-solid fa-file-word text-blue-500" />
                  ) : isSheet ? (
                    <i className="fa-solid fa-file-excel text-green-600" />
                  ) : (
                    <i className="fa-solid fa-file-lines" />
                  )}
                </div>
              )}
              <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                <button type="button" onClick={() => onRemove(index)}>
                  <i className="fa-solid fa-xmark text-white text-lg" />
                </button>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export const LeftBubbleSkeleton = () => (
  <div className="flex mb-4 w-[900px]">
    <Skeleton variant="circular" width={40} height={40}>
      <Avatar />
    </Skeleton>
    <div>
      <div
        style={{ boxShadow: "0px 0px 18px 0px rgba(0,0,0,0.07)" }}
        className="flex bg-white rounded-lg px-4 py-3 ml-4"
      >
        <Skeleton variant="text" width={700} height={20} />
        <Skeleton variant="circular" width={20} height={20} className="ml-3" />
      </div>
      <Skeleton variant="text" width={50} height={15} className="ml-4 mt-2" />
    </div>
  </div>
);

export const RightBubbleSkeleton = () => (
  <div className="flex justify-end mb-4 w-[900px]">
    <div>
      <div className="flex bg-[#EAF0FF] rounded-lg px-4 py-3 mr-4">
        <Skeleton variant="circular" width={20} height={20} />
        <Skeleton variant="text" width={700} height={20} className="ml-3" />
      </div>
      <Skeleton variant="text" width={50} height={15} className="ml-6 mt-2 text-right" />
    </div>
    <Skeleton variant="circular" width={40} height={40}>
      <Avatar />
    </Skeleton>
  </div>
);
